import type { QueryClient } from '@tanstack/react-query'
import { queryOptions } from '@tanstack/react-query'
import { authClient } from './auth'
import { workspacesQueryOptions } from './workspace'

/** One cached source of truth for "who am I". */
export const sessionQueryOptions = queryOptions({
  queryKey: ['session'],
  queryFn: async () => {
    const { data } = await authClient.getSession()
    return data ?? null
  },
  staleTime: 5 * 60 * 1000,
})

/**
 * Used by every route guard. `revalidateIfStale` keeps a long-lived tab from
 * drifting, but note it only schedules a *background* refetch — the call still
 * resolves with whatever is cached.
 */
export function ensureSession(queryClient: QueryClient) {
  return queryClient.ensureQueryData({ ...sessionQueryOptions, revalidateIfStale: true })
}

/**
 * Call after any sign-in, sign-up or sign-out, and await it before navigating.
 *
 * Removing the entry rather than invalidating it is the whole point. Nothing
 * subscribes to this query with a hook, so it has no active observer:
 * `invalidateQueries` would mark it stale without refetching, and
 * `ensureQueryData` hands back stale data rather than waiting. The guard would
 * then read the pre-login value and bounce the user straight back to /login,
 * which only a hard refresh cleared. With no cache entry, `ensureQueryData`
 * has no choice but to fetch and await.
 */
export async function refreshSession(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: sessionQueryOptions.queryKey })
  // Whoever just signed in is not who the workspace list was fetched for.
  queryClient.removeQueries({ queryKey: workspacesQueryOptions.queryKey })
  await queryClient.ensureQueryData(sessionQueryOptions)
}
